import { EditorIcon } from "@/components/environments/EditorIcon";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Label } from "@/components/podkit/forms/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { Switch } from "@/components/Switch";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useSetDefaultEditor } from "@/hooks/use-default-editor";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useGetDotfilesConfiguration, useSetDotfilesConfiguration } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC } from "react";

export const UserPreferencesPage: FC = () => {
    useDocumentTitle("Preferences");

    return (
        <div className="flex max-w-[580px] flex-col gap-6" data-testid="user-preferences-page">
            <Appearance />
            <DefaultEditor />
            <AgentNotifications />
            <DotfilesConfiguration />
        </div>
    );
};

const Appearance: FC = () => {
    return (
        <InputField label="Appearance" hint="Customize how Ona looks on your device">
            <ThemeToggle />
        </InputField>
    );
};

const DefaultEditor: FC = () => {
    const setDefaultEditor = useSetDefaultEditor();
    const { selectedEditor, availableEditors, isPending: isPendingEditors } = useGetEditorForEnv(undefined);

    return (
        <InputField
            label="Default Editor"
            hint={
                <span>
                    This will be your default IDE on new projects.{" "}
                    <ExternalLink href={"https://ona.com/docs/ona/editors/overview"} className="text-sm">
                        Learn more
                    </ExternalLink>
                    .
                </span>
            }
        >
            <Select
                name="defaultEditor"
                value={selectedEditor?.id}
                onValueChange={(editorId) => setDefaultEditor(editorId)}
            >
                <SelectTrigger loading={isPendingEditors}>
                    <SelectValue>
                        <div className="flex items-center gap-2">
                            <EditorIcon editor={selectedEditor!} size="base" />
                            <span>{selectedEditor?.name}</span>
                        </div>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {(availableEditors || []).map((editor) => (
                        <SelectItem key={editor.id} value={editor.id}>
                            <div className="flex items-center gap-2">
                                <EditorIcon editor={editor} size="base" />
                                <span>{editor.name}</span>
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </InputField>
    );
};

const AgentNotifications: FC = () => {
    const { value: agentNotificationValue, setValue: setAgentNotificationValue } =
        usePreferenceValue("agent-done-notification");

    // Convert string preference to boolean (sound = true, none/undefined = false)
    const isEnabled = agentNotificationValue === "sound";

    const handleToggle = useCallback(
        (checked: boolean) => {
            setAgentNotificationValue(checked ? "sound" : "none");
        },
        [setAgentNotificationValue],
    );

    return (
        <div className="flex flex-col gap-3">
            <div className="flex gap-4">
                <div className="flex">
                    <Switch
                        state={isEnabled ? "checked" : "unchecked"}
                        onToggle={handleToggle}
                        id="agent-done-notification-toggle"
                        aria-label="Play sound when agent is done"
                    />
                </div>
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <Label className="text-base">Agent done notification</Label>
                        <Text className="text-sm text-content-secondary">Play sound when agent is done</Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DotfilesConfiguration: FC = () => {
    const { data: dotfilesConfiguration, isPending: isPendingDotfilesConfiguration } = useGetDotfilesConfiguration();
    const setDotfilesConfiguration = useSetDotfilesConfiguration();
    const { toast } = useToast();

    const [dotfilesRepositoryError, setDotfilesRepositoryError] = useState<string | null>(null);
    const [dotfilesRepository, setDotfilesRepository] = useState("");
    const [savedDotfilesRepository, setSavedDotfilesRepository] = useState("");
    useEffect(() => {
        if (isPendingDotfilesConfiguration) {
            return;
        }
        setDotfilesRepository(dotfilesConfiguration?.repository || "");
        setSavedDotfilesRepository(dotfilesConfiguration?.repository || "");
    }, [dotfilesConfiguration?.repository, isPendingDotfilesConfiguration]);

    const updateDotfilesConfiguration = useCallback(
        async (repositoryUrl: string) => {
            // If the repository URL hasn't changed, don't try to update it
            // E.g. if focus and de-focus the input, then nothing should happen.
            if (savedDotfilesRepository === repositoryUrl) {
                return;
            }

            try {
                await setDotfilesConfiguration.mutateAsync({ repositoryUrl });
                setSavedDotfilesRepository(repositoryUrl);
                toast({ title: "Dotfiles repository updated" });
            } catch (error) {
                toast({
                    title: "Failed to update dotfiles repository",
                    description: formatError(error),
                });
            }
        },
        [savedDotfilesRepository, setDotfilesConfiguration, toast],
    );

    return (
        <InputField
            label="Dotfiles repository"
            error={dotfilesRepositoryError}
            hint={
                <span>
                    URL of a dotfiles Git repository.{" "}
                    <ExternalLink href={"https://ona.com/docs/ona/configuration/dotfiles/overview"} className="text-sm">
                        Learn more
                    </ExternalLink>
                    .
                </span>
            }
        >
            <Input
                disabled={isPendingDotfilesConfiguration || setDotfilesConfiguration.isPending}
                value={dotfilesRepository}
                type="url"
                placeholder="https://github.com/your-username/dotfiles"
                onChange={(e) => setDotfilesRepository(e.target.value)}
                data-testid="dotfiles-configuration-input"
                onBlur={(e) => {
                    if (e.target instanceof HTMLInputElement) {
                        const isValid = e.target.checkValidity();
                        setDotfilesRepositoryError(isValid ? null : "Invalid repository URL");
                        if (isValid) {
                            void updateDotfilesConfiguration(dotfilesRepository ?? "");
                        }
                    }
                }}
            />
        </InputField>
    );
};
